import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Calendar, MessageSquare, PhoneCall, Video, Play, Star, CheckCircle2, Users, ArrowRight, Trash2 } from "lucide-react";
import NudgeToggle from "@user/components/NudgeToggle";
import { RescheduleButton } from "@user/components/RescheduleButton";
import { photoSrc } from "@shared/photoSrc";

export const TYPE_META = {
  chat: { icon: MessageSquare, label: "Chat", color: "text-[#1E3A8A]" },
  audio: { icon: PhoneCall, label: "Audio", color: "text-emerald-700" },
  video: { icon: Video, label: "Video", color: "text-purple-700" },
};

export function parseSlot(c) {
  if (!c?.slot_date || !c?.slot_time) return null;
  // slot_time may be "14:30" or "14:30-14:45". Take the start.
  const start = String(c.slot_time).split(/[–-]/)[0].trim();
  const iso = `${c.slot_date}T${start}:00`;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d;
}

// Returns one of: 'incomplete' | 'upcoming' | 'live' | 'completed' | 'cancelled'
// Backend status is authoritative. A booking is only "booked" once paid; it only
// goes Live once the lawyer starts it (status live) or accepts it — immediately
// for instant bookings, from the slot start time for scheduled ones.
const CLOSED = { completed: "completed", cancelled: "cancelled", rejected: "cancelled", no_show: "cancelled", lawyer_no_show: "cancelled" };

export function lifecycleState(c, now = new Date()) {
  if (CLOSED[c.status]) return CLOSED[c.status];
  if (c.payment_status === "refunded") return "cancelled";
  if (c.payment_status !== "paid") return "incomplete";
  if (c.status === "live") return "live";
  if (c.status === "accepted") {
    const slot = parseSlot(c);
    if (c.booking_type === "instant" || !slot || now >= slot) return "live";
  }
  return "upcoming";
}

function formatRelative(target, now = new Date()) {
  const diffMs = target.getTime() - now.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 60 && mins >= 0) return `Starts in ${mins} min${mins === 1 ? "" : "s"}`;
  if (mins < 0) return "Slot time reached · waiting for the lawyer to start";
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Starts in ${hours}h ${mins % 60}m`;
  const days = Math.floor(hours / 24);
  return `In ${days} day${days === 1 ? "" : "s"}`;
}

export function formatSlot(c) {
  const d = parseSlot(c);
  if (!d) return c.slot_date || "Time TBD";
  const opts = { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true };
  return d.toLocaleString("en-IN", opts);
}

const RANK = { live: 0, upcoming: 1, incomplete: 2, completed: 3, cancelled: 4 };

// Live first, then upcoming (closest first), then incomplete/completed/cancelled (most recent first).
export function sortConsults(consults) {
  return [...(consults || [])].sort((a, b) => {
    const ra = RANK[lifecycleState(a)], rb = RANK[lifecycleState(b)];
    if (ra !== rb) return ra - rb;
    const sa = parseSlot(a)?.getTime() || Date.parse(a.created_at || 0) || 0;
    const sb = parseSlot(b)?.getTime() || Date.parse(b.created_at || 0) || 0;
    return ra >= 2 ? sb - sa : sa - sb;
  });
}

function useMinuteTick() {
  const [, tick] = useState(0);
  // Re-render every 30s so "Starts in X" + Live state update without a refresh
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 30000);
    return () => clearInterval(id);
  }, []);
}

const INSTANT_WAIT_MIN = 10;
// Mirrors backend lawyer_no_show: an unstarted instant booking auto-closes (refund) 10 min after payment.
export function instantWaitLabel(c, now = new Date()) {
  if (c.booking_type !== "instant" || !c.paid_at) return "Instant · waiting for the lawyer to start";
  const left = Math.ceil((new Date(c.paid_at).getTime() + INSTANT_WAIT_MIN * 60000 - now.getTime()) / 60000);
  if (left <= 0) return "Lawyer hasn't started · closing with an automatic refund…";
  return `Waiting for the lawyer · auto-refund if not started in ${left} min`;
}

export function ConsultationsEmptyState({ testid = "upcoming-empty", title = "No consultations yet." }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
      <div className="h-12 w-12 mx-auto rounded-2xl bg-[#FBF4DF] text-[#8a6d14] grid place-items-center">
        <Users size={22} />
      </div>
      <p className="mt-3 text-sm text-slate-500">{title}</p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        <Button asChild variant="outline" className="rounded-full font-semibold text-xs h-9 px-4" data-testid={`${testid}-find`}>
          <Link to="/lawyers">Find a Lawyer</Link>
        </Button>
        <Button asChild className="rounded-full adv-btn-gold font-bold text-xs h-9 px-4" data-testid={`${testid}-consult`}>
          <Link to="/lawyers">Consult Now <ArrowRight size={13} /></Link>
        </Button>
      </div>
    </div>
  );
}

const FILTERS = [
  { key: "all", label: "All" }, { key: "live", label: "Live" }, { key: "upcoming", label: "Upcoming" },
  { key: "incomplete", label: "Incomplete" }, { key: "completed", label: "Completed" }, { key: "cancelled", label: "Cancelled" },
];
const SECTION_TITLE = { live: "Live now", upcoming: "Upcoming Consultations", incomplete: "Incomplete bookings", completed: "Completed", cancelled: "Cancelled" };
const STATES = ["live", "upcoming", "incomplete", "completed", "cancelled"];

// Complete consultation history (Consultations tab) with status filters.
export default function UpcomingConsultations({ consults, onDiscard, onRescheduled }) {
  useMinuteTick();
  const [filter, setFilter] = useState("all");

  if (!consults || consults.length === 0) return <ConsultationsEmptyState />;

  const sorted = sortConsults(consults);
  const counts = sorted.reduce((acc, c) => { const st = lifecycleState(c); acc[st] = (acc[st] || 0) + 1; return acc; }, {});
  const groups = STATES
    .filter((st) => filter === "all" || filter === st)
    .map((st) => ({ st, items: sorted.filter((c) => lifecycleState(c) === st) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2" data-testid="consult-filters" role="tablist">
        {FILTERS.map((f) => (
          <button key={f.key} type="button" role="tab" aria-selected={filter === f.key} onClick={() => setFilter(f.key)}
            data-testid={`consult-filter-${f.key}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${filter === f.key ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0A2540]/40"}`}>
            {f.label}{f.key !== "all" && counts[f.key] ? ` · ${counts[f.key]}` : ""}
          </button>
        ))}
      </div>
      {groups.length === 0 && (
        <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-2xl p-6 text-center" data-testid="consult-filter-empty">
          No {FILTERS.find((f) => f.key === filter)?.label.toLowerCase()} consultations.
        </p>
      )}
      {groups.map(({ st, items }) => (
        <section key={st} data-testid={`${st}-section`}>
          <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-500 mb-3">{SECTION_TITLE[st]}</h3>
          <div className="space-y-3">
            {items.map((c) => <ConsultCard key={c.id} consult={c} onDiscard={onDiscard} onRescheduled={onRescheduled} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

// Dashboard priority view: Live → Upcoming → Incomplete bookings → View all. History stays in the Consultations tab.
export function CurrentConsultations({ consults, onViewAll, onDiscard, onRescheduled }) {
  useMinuteTick();
  const sorted = sortConsults(consults);
  const live = sorted.filter((c) => lifecycleState(c) === "live");
  const upcoming = sorted.filter((c) => lifecycleState(c) === "upcoming");
  const incomplete = sorted.filter((c) => lifecycleState(c) === "incomplete");
  return (
    <div className="space-y-6" data-testid="current-consultations">
      {live.length > 0 && (
        <section data-testid="dashboard-live-section">
          <h3 className="text-sm font-extrabold uppercase tracking-wider text-emerald-700 mb-3 inline-flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Live now
          </h3>
          <div className="space-y-3">{live.map((c) => <ConsultCard key={c.id} consult={c} onRescheduled={onRescheduled} />)}</div>
        </section>
      )}
      {upcoming.length > 0 && (
        <section data-testid="dashboard-upcoming-section">
          <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-500 mb-3">Upcoming Consultations</h3>
          <div className="space-y-3">{upcoming.map((c) => <ConsultCard key={c.id} consult={c} onRescheduled={onRescheduled} />)}</div>
        </section>
      )}
      {incomplete.length > 0 && (
        <section data-testid="dashboard-incomplete-section">
          <h3 className="text-sm font-extrabold uppercase tracking-wider text-amber-700 mb-3">Incomplete bookings</h3>
          <div className="space-y-3">{incomplete.map((c) => <ConsultCard key={c.id} consult={c} onDiscard={onDiscard} />)}</div>
        </section>
      )}
      {live.length === 0 && upcoming.length === 0 && incomplete.length === 0 && (
        <ConsultationsEmptyState testid="dashboard-consults-empty" title="No live or upcoming consultations right now." />
      )}
      {(consults || []).length > 0 && (
        <div className="text-right">
          <button type="button" onClick={onViewAll} data-testid="view-all-consultations"
            className="text-sm font-bold text-[#1E3A8A] hover:underline inline-flex items-center gap-1">
            View all consultations <ArrowRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

export function ConsultCard({ consult, onDiscard, onRescheduled }) {
  const state = lifecycleState(consult);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const started = consult.status === "live";
  const instant = consult.booking_type === "instant";
  const meta = TYPE_META[consult.consultation_type] || TYPE_META.chat;
  const Icon = meta.icon;
  const slotDate = parseSlot(consult);

  return (
    <div
      data-testid={`consult-card-${consult.id}`}
      className={`bg-white border rounded-2xl p-4 md:p-5 transition-all ${
        state === "live" ? "border-emerald-500 shadow-lg ring-2 ring-emerald-100" : "border-slate-200 hover:shadow-md"
      }`}
    >
      <div className="flex items-start gap-3 md:gap-4">
        {consult.lawyer_photo ? (
          <img src={photoSrc(consult.lawyer_photo, "sm")} alt={consult.lawyer_name} className="h-12 w-12 md:h-14 md:w-14 rounded-xl object-cover shrink-0" />
        ) : (
          <div className="h-12 w-12 md:h-14 md:w-14 rounded-xl adv-navy text-[#D4AF37] grid place-items-center font-bold shrink-0">
            {(consult.lawyer_name || "L")[0]}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <p className="font-extrabold text-[#0A2540] truncate" style={{ fontFamily: "Poppins" }}>
                {consult.lawyer_name}
              </p>
              <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                <Icon size={12} className={meta.color} />
                <span className="font-semibold">{meta.label}</span>
                <span className="text-slate-300">·</span>
                <Calendar size={11} />
                <span>{formatSlot(consult)}</span>
              </p>
            </div>
            <StatusBadge state={state} />
          </div>

          {/* Context row: time-remaining message OR live banner */}
          {state === "upcoming" && !instant && slotDate && (
            <p
              data-testid={`consult-countdown-${consult.id}`}
              className="mt-2 text-xs text-slate-600 inline-flex items-center gap-1 bg-slate-50 rounded-full px-2.5 py-1"
            >
              <Clock size={11} /> {formatRelative(slotDate)}
              {consult.rescheduled_count > 0 && <span className="ml-1 text-[10px] uppercase tracking-wider font-bold text-[#8a6d1a]" data-testid={`consult-rescheduled-${consult.id}`}>· Rescheduled</span>}
            </p>
          )}
          {state === "live" && (
            <p className="mt-2 text-xs text-emerald-700 font-bold inline-flex items-center gap-1.5 bg-emerald-50 rounded-full px-2.5 py-1" data-testid={`consult-live-pill-${consult.id}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {started ? "Live now — your lawyer is in the room" : "Live now — your lawyer has accepted, join the room"}
            </p>
          )}
          {state === "upcoming" && (instant || !slotDate) && (
            <p className="mt-2 text-xs text-slate-600 inline-flex items-center gap-1 bg-slate-50 rounded-full px-2.5 py-1" data-testid={`consult-waiting-${consult.id}`}>
              <Clock size={11} /> {instant ? instantWaitLabel(consult) : "Waiting for the lawyer to start"}
            </p>
          )}
          {consult.status === "lawyer_no_show" && (
            <p className="mt-2 text-xs text-rose-700 font-bold inline-flex items-center gap-1 bg-rose-50 rounded-full px-2.5 py-1" data-testid={`consult-lawyer-no-show-${consult.id}`}>
              Lawyer didn't {instant ? "start in time" : "join"} · {consult.payment_status === "refunded" ? "Refunded" : "Refund will be processed"}
            </p>
          )}
          {state === "incomplete" && (
            <p className="mt-2 text-[11px] text-amber-700 font-bold" data-testid={`consult-incomplete-${consult.id}`}>
              Incomplete booking · payment pending — the lawyer is notified only after payment
            </p>
          )}

          {/* CTAs */}
          <div className="mt-3 flex flex-wrap gap-2">
            {state === "live" && (
              <Button
                asChild
                data-testid={started ? `rejoin-consult-${consult.id}` : `join-consult-${consult.id}`}
                className={`rounded-full font-bold text-xs h-10 px-5 ${started ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-md" : "adv-btn-gold"}`}
              >
                <Link to={`/consultations/${consult.id}`}>
                  <Play size={13} fill="currentColor" /> {started ? "Rejoin Consultation" : "Join Consultation"}
                </Link>
              </Button>
            )}
            {state === "cancelled" && (
              <Button asChild variant="outline" data-testid={`view-consult-${consult.id}`} className="rounded-full font-semibold text-xs h-9 px-4">
                <Link to={`/consultations/${consult.id}`}>View details <ArrowRight size={13} /></Link>
              </Button>
            )}
            {consult.status === "lawyer_no_show" && instant && (
              <Button asChild data-testid={`find-another-lawyer-${consult.id}`} className="rounded-full adv-btn-gold font-bold text-xs h-9 px-4">
                <Link to="/lawyers?available_only=true">Find another lawyer online now <ArrowRight size={13} /></Link>
              </Button>
            )}
            {state === "upcoming" && (
              <>
                <Button
                  asChild
                  variant="outline"
                  data-testid={`view-consult-${consult.id}`}
                  className="rounded-full border-[#0A2540]/20 hover:bg-[#0A2540] hover:text-white font-semibold text-xs h-9 px-4"
                >
                  <Link to={`/consultations/${consult.id}`}>
                    View details <ArrowRight size={13} />
                  </Link>
                </Button>
                <NudgeToggle consult={consult} slotDate={slotDate} />
                {!instant && slotDate && <RescheduleButton consult={consult} onRescheduled={onRescheduled} />}
              </>
            )}
            {state === "incomplete" && (
              <Button
                asChild
                data-testid={`pay-consult-${consult.id}`}
                className="rounded-full adv-btn-primary font-bold text-xs h-9 px-4"
              >
                <Link to={`/consultations/${consult.id}`}>
                  Complete payment <ArrowRight size={13} />
                </Link>
              </Button>
            )}
            {state === "incomplete" && onDiscard && !confirmDiscard && (
              <Button type="button" variant="outline" onClick={() => setConfirmDiscard(true)} data-testid={`discard-consult-${consult.id}`}
                className="rounded-full font-semibold text-xs h-9 px-4 text-slate-600 hover:text-rose-700 hover:border-rose-300">
                <Trash2 size={13} /> Discard
              </Button>
            )}
            {state === "incomplete" && onDiscard && confirmDiscard && (
              <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`discard-confirm-${consult.id}`}>
                <span className="text-slate-600">Discard this booking?</span>
                <Button type="button" disabled={discarding} data-testid={`discard-confirm-yes-${consult.id}`}
                  onClick={async () => { setDiscarding(true); try { await onDiscard(consult.id); } finally { setDiscarding(false); setConfirmDiscard(false); } }}
                  className="rounded-full bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs h-8 px-3">
                  {discarding ? "Discarding…" : "Yes, discard"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setConfirmDiscard(false)} data-testid={`discard-confirm-no-${consult.id}`}
                  className="rounded-full text-xs h-8 px-3">Keep</Button>
              </div>
            )}
            {state === "completed" && (
              <>
                <Button
                  asChild
                  variant="outline"
                  data-testid={`view-consult-${consult.id}`}
                  className="rounded-full font-semibold text-xs h-9 px-4"
                >
                  <Link to={`/consultations/${consult.id}`}>
                    <CheckCircle2 size={13} /> View summary
                  </Link>
                </Button>
                {consult.lcr_state === "published" ? (
                  <Button
                    asChild
                    data-testid={`view-record-${consult.id}`}
                    className="rounded-full adv-btn-navy font-bold text-xs h-9 px-4"
                  >
                    <Link to={`/consultations/${consult.id}/record`}>
                      View record
                    </Link>
                  </Button>
                ) : (
                  <Badge
                    className="rounded-full font-bold uppercase tracking-wider text-[10px] bg-amber-50 text-amber-700 border border-amber-200"
                    data-testid={`awaiting-record-${consult.id}`}
                  >
                    Awaiting record
                  </Badge>
                )}
                <Button
                  asChild
                  data-testid={`review-consult-${consult.id}`}
                  className="rounded-full adv-btn-gold font-bold text-xs h-9 px-4"
                >
                  <Link to={`/consultations/${consult.id}?review=1`}>
                    <Star size={13} fill="currentColor" /> Leave Review
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ state }) {
  if (state === "live")
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 rounded-full font-bold uppercase tracking-wider text-[10px]" data-testid="badge-live">
        Live
      </Badge>
    );
  if (state === "upcoming")
    return (
      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 rounded-full font-bold uppercase tracking-wider text-[10px]" data-testid="badge-upcoming">
        Upcoming
      </Badge>
    );
  if (state === "incomplete")
    return (
      <Badge className="bg-amber-50 text-amber-800 hover:bg-amber-50 border border-amber-200 rounded-full font-bold uppercase tracking-wider text-[10px]" data-testid="badge-incomplete">
        Incomplete
      </Badge>
    );
  if (state === "cancelled")
    return (
      <Badge className="bg-rose-50 text-rose-700 hover:bg-rose-50 rounded-full font-bold uppercase tracking-wider text-[10px]" data-testid="badge-cancelled">
        Cancelled
      </Badge>
    );
  return (
    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 rounded-full font-bold uppercase tracking-wider text-[10px]" data-testid="badge-completed">
      Completed
    </Badge>
  );
}
